import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BoltIcon from "@mui/icons-material/Bolt";
import { MerchantKpis } from "../../domain/merchant";
import { getMerchantKpis } from "../../services/api/merchant";

const KPI_CONFIG = [
  { key: "awaitingAction" as const, label: "AWAITING ACTION", icon: <WarningAmberOutlinedIcon />, color: "warning" as const },
  { key: "transitVolume" as const, label: "TRANSIT VOLUME", icon: <LocalShippingOutlinedIcon />, color: "primary" as const },
  { key: "retentionRate" as const, label: "RETENTION RATE", icon: <TrendingUpIcon />, color: "success" as const, suffix: "%" },
  { key: "automationPct" as const, label: "AUTOMATION %", icon: <BoltIcon />, color: "primary" as const, suffix: "%" },
];

export default function MerchantOverview() {
  const { tenantSlug = "" } = useParams();
  const [kpis, setKpis] = useState<MerchantKpis | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getMerchantKpis(tenantSlug);
        if (mounted) setKpis(data);
      } catch {
        if (mounted) setKpis(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [tenantSlug]);

  const chart = useMemo(
    () => [
      { day: "Mon", h: 85 },
      { day: "Tue", h: 130 },
      { day: "Wed", h: 105 },
      { day: "Thu", h: 155 },
      { day: "Fri", h: 210, active: true },
      { day: "Sat", h: 165 },
      { day: "Sun", h: 120 },
    ],
    []
  );

  const formatValue = (key: string, kpis: MerchantKpis) => {
    if (key === "retentionRate") return kpis.retentionRate.toFixed(1);
    if (key === "automationPct") return kpis.automationPct;
    if (key === "awaitingAction") return kpis.awaitingAction;
    return kpis.transitVolume;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ mr: 2 }} />
        <Typography color="text.secondary" fontWeight={600}>Loading KPIs...</Typography>
      </Box>
    );
  }

  if (!kpis) {
    return (
      <Card>
        <CardContent>
          <Typography fontWeight={700}>Could not load KPIs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            If backend is down, set <strong>REACT_APP_USE_MOCKS=true</strong>.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: "100%", height: "100%" }}>
      {/* KPI Cards */}
      <Grid container spacing={2}>
        {KPI_CONFIG.map((cfg) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={cfg.key}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: `${cfg.color}.light`,
                      color: `${cfg.color}.main`,
                      flexShrink: 0,
                    }}
                  >
                    {cfg.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {cfg.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                      {formatValue(cfg.key, kpis)}{cfg.suffix ?? ""}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Lower Analytics Row */}
      <Grid container spacing={2} sx={{ flex: 1 }}>
        {/* Return Trajectory */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Return Trajectory</Typography>
                  <Typography variant="caption" color="text.secondary">Weekly return volume snapshot</Typography>
                </Box>
                <Button variant="outlined" size="small">Export report</Button>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 240,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 1.5,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: "grey.50",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                {chart.map((c) => (
                  <Stack key={c.day} alignItems="center" spacing={1} sx={{ flex: "1 1 0", minWidth: 28 }}>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 36,
                        height: c.h,
                        borderRadius: 2.5,
                        bgcolor: c.active ? "primary.main" : "primary.light",
                        opacity: c.active ? 1 : 0.6,
                        transition: "transform 0.12s",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {c.day}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Guard */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Financial Guard</Typography>
                <Typography variant="caption" color="text.secondary">Estimated value retained through Store Credit</Typography>
              </Box>

              <Box sx={{ p: 2, borderRadius: 3, border: "1px dashed", borderColor: "divider", bgcolor: "grey.50", mb: 2 }}>
                <Typography variant="overline" color="text.secondary">TOTAL SAVINGS</Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                  <Typography variant="h4" fontWeight={800}>
                    SAR {((kpis.financialGuard?.totalSavingsSar ?? 0) / 1000).toFixed(0)}k
                  </Typography>
                  {kpis.financialGuard?.deltaPct != null && (
                    <Chip
                      label={`${kpis.financialGuard.deltaPct > 0 ? "+" : ""}${kpis.financialGuard.deltaPct}%`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 1.5, borderRadius: 3, border: 1, borderColor: "divider" }}
                >
                  <Typography variant="body2" fontWeight={700}>Cash Refunded</Typography>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    -SAR {(kpis.financialGuard?.cashRefundedSar ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 1.5, borderRadius: 3, border: 1, borderColor: "divider" }}
                >
                  <Typography variant="body2" fontWeight={700}>Credit Issued</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    +SAR {(kpis.financialGuard?.creditIssuedSar ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>

              <Button variant="outlined" fullWidth sx={{ mt: "auto" }}>
                View Analytics
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
